import { useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

import { Brand, Colors, Radii, Spacing } from '@/constants/theme';
import type { ActivityCount } from '@/types/history';

interface PrintStatusCardProps {
  totalSelected: number;
  printedCount: number;
  needsPrintCount: number;
  periodLabel: string;
}

interface WorksheetDistributionCardProps {
  totalSelected: number;
  activityCounts: ActivityCount[];
}

interface CollapsibleCardProps {
  title: string;
  scope: string;
  summary: string;
  children: ReactNode;
}

const DONUT_SIZE = 148;
const DONUT_STROKE = 18;
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_CENTER = DONUT_SIZE / 2;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
const DONUT_COLORS = [
  Brand.primary,
  Brand.accent,
  Brand.teal,
  Brand.info,
  Brand.success,
  Brand.primaryLight,
] as const;

export function PrintStatusCard({
  totalSelected,
  printedCount,
  needsPrintCount,
  periodLabel,
}: PrintStatusCardProps) {
  const summaryLabel = `Cetak worksheet ${periodLabel}: ${printedCount} sudah dicetak, ${needsPrintCount} perlu dicetak`;

  return (
    <View
      style={styles.card}
      accessibilityRole="summary"
      accessibilityLabel={summaryLabel}
    >
      <View style={styles.cardHeader}>
        <View style={styles.cardHeaderCopy}>
          <Text style={styles.cardTitle}>Cetak worksheet</Text>
          <Text style={styles.cardScope}>{periodLabel} · {totalSelected} worksheet</Text>
        </View>
      </View>
      <View style={styles.cardBody}>
        <View style={styles.metricRow}>
          <View style={styles.metric}>
            <Text style={[styles.metricValue, styles.printedValue]}>{printedCount}</Text>
            <Text style={styles.metricLabel}>sudah dicetak</Text>
          </View>
          <View style={styles.metricDivider} accessibilityElementsHidden />
          <View style={styles.metric}>
            <Text style={[styles.metricValue, styles.needsPrintValue]}>{needsPrintCount}</Text>
            <Text style={styles.metricLabel}>perlu dicetak</Text>
          </View>
        </View>
      </View>
    </View>
  );
}

export function WorksheetDistributionCard({
  totalSelected,
  activityCounts,
}: WorksheetDistributionCardProps) {
  const rows = activityCounts.filter((row) => row.count > 0);
  const countedTotal = rows.reduce((sum, row) => sum + row.count, 0);
  const total = totalSelected > 0 ? totalSelected : countedTotal;

  if (total === 0) return null;

  // ponytail: bounded by the small activity-label catalog; precompute only if it grows.
  const segments = rows.map((row, index) => {
    const length = (row.count / total) * DONUT_CIRCUMFERENCE;
    const offset = rows
      .slice(0, index)
      .reduce((sum, previous) => sum + (previous.count / total) * DONUT_CIRCUMFERENCE, 0);
    return {
      ...row,
      color: DONUT_COLORS[index % DONUT_COLORS.length],
      length,
      offset,
    };
  });
  const chartSummary = rows
    .map((row) => `${row.activityLabel} ${row.count}`)
    .join(', ');

  return (
    <CollapsibleCard
      title="Pilihan worksheet"
      scope="Sepanjang aktivitas"
      summary={`${total} worksheet · ${rows.length} jenis`}
    >
      {rows.length > 0 ? (
        <View style={styles.chartRow}>
          <View
            style={styles.donutWrap}
            accessible
            accessibilityRole="image"
            accessibilityLabel={`Distribusi jenis worksheet: ${chartSummary}`}
          >
            <Svg width={DONUT_SIZE} height={DONUT_SIZE} viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}>
              <Circle
                cx={DONUT_CENTER}
                cy={DONUT_CENTER}
                r={DONUT_RADIUS}
                fill="none"
                stroke={Colors.light.border}
                strokeWidth={DONUT_STROKE}
              />
              {segments.map((segment) => (
                <Circle
                  key={segment.activityLabel}
                  cx={DONUT_CENTER}
                  cy={DONUT_CENTER}
                  r={DONUT_RADIUS}
                  fill="none"
                  stroke={segment.color}
                  strokeWidth={DONUT_STROKE}
                  strokeDasharray={`${segment.length} ${DONUT_CIRCUMFERENCE - segment.length}`}
                  strokeDashoffset={-segment.offset}
                  strokeLinecap="butt"
                  transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}
                />
              ))}
            </Svg>
            <View pointerEvents="none" style={styles.donutCenter}>
              <Text style={styles.donutTotal}>{total}</Text>
              <Text style={styles.donutLabel}>dipilih</Text>
            </View>
          </View>
          <View style={styles.legend}>
            {segments.map((segment) => {
              const percentage = Math.round((segment.count / total) * 100);
              return (
                <View
                  key={segment.activityLabel}
                  style={styles.legendRow}
                  accessible
                  accessibilityLabel={`${segment.activityLabel}: ${segment.count} worksheet, ${percentage} persen`}
                >
                  <View style={[styles.legendDot, { backgroundColor: segment.color }]} />
                  <Text style={styles.legendLabel} numberOfLines={2}>
                    {segment.activityLabel}
                  </Text>
                  <Text style={styles.legendCount}>{segment.count}</Text>
                </View>
              );
            })}
          </View>
        </View>
      ) : (
        <Text style={styles.emptyChartText}>Detail jenis worksheet belum tersedia.</Text>
      )}
    </CollapsibleCard>
  );
}

function CollapsibleCard({ title, scope, summary, children }: CollapsibleCardProps) {
  const [expanded, setExpanded] = useState(false);
  const actionLabel = expanded ? 'Tutup detail' : 'Buka detail';

  return (
    <View style={styles.card}>
      <Pressable
        onPress={() => setExpanded((value) => !value)}
        style={({ pressed }) => [styles.cardHeader, pressed && styles.cardHeaderPressed]}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${summary}`}
        accessibilityHint={`Ketuk untuk ${actionLabel.toLowerCase()}`}
        accessibilityState={{ expanded }}
      >
        <View style={styles.cardHeaderCopy}>
          <Text style={styles.cardTitle}>{title}</Text>
          <Text style={styles.cardScope}>{expanded ? scope : summary}</Text>
        </View>
        <Text style={styles.cardToggle} aria-hidden={true}>
          {expanded ? '⌃' : '⌄'}
        </Text>
      </Pressable>
      {expanded ? <View style={styles.cardBody}>{children}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.light.card,
    borderColor: Colors.light.border,
    borderRadius: Radii.medium,
    borderWidth: 1,
    overflow: 'hidden',
  },
  cardHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: Spacing.three,
    minHeight: 72,
    padding: Spacing.four,
  },
  cardHeaderPressed: {
    opacity: 0.72,
  },
  cardHeaderCopy: {
    flex: 1,
    gap: Spacing.one,
    minWidth: 0,
  },
  cardToggle: {
    color: Brand.primary,
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 28,
    textAlign: 'center',
    width: 28,
  },
  cardBody: {
    gap: Spacing.three,
    paddingHorizontal: Spacing.four,
    paddingBottom: Spacing.four,
  },
  cardTitle: {
    color: Colors.light.text,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
  },
  cardScope: {
    color: Colors.light.textSecondary,
    fontSize: 13,
    lineHeight: 19,
  },
  metricRow: {
    alignItems: 'stretch',
    flexDirection: 'row',
    gap: Spacing.three,
  },
  metric: {
    flex: 1,
    gap: Spacing.one,
  },
  metricDivider: {
    backgroundColor: Colors.light.border,
    width: 1,
  },
  metricValue: {
    fontSize: 28,
    fontWeight: '700',
    lineHeight: 34,
  },
  printedValue: {
    color: Brand.primary,
  },
  needsPrintValue: {
    color: Brand.accent,
  },
  metricLabel: {
    color: Colors.light.textSecondary,
    fontSize: 13,
    lineHeight: 19,
  },
  chartRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: Spacing.three,
  },
  donutWrap: {
    alignItems: 'center',
    height: DONUT_SIZE,
    justifyContent: 'center',
    position: 'relative',
    width: DONUT_SIZE,
  },
  donutCenter: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
  },
  donutTotal: {
    color: Colors.light.text,
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 29,
  },
  donutLabel: {
    color: Colors.light.textSecondary,
    fontSize: 11,
    lineHeight: 16,
  },
  legend: {
    flex: 1,
    gap: Spacing.two,
    minWidth: 0,
  },
  legendRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: Spacing.one,
    minHeight: 24,
  },
  legendDot: {
    borderRadius: Radii.pill,
    height: 10,
    width: 10,
  },
  legendLabel: {
    color: Colors.light.textSecondary,
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
  },
  legendCount: {
    color: Colors.light.text,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 19,
  },
  emptyChartText: {
    color: Colors.light.textSecondary,
    fontSize: 13,
    lineHeight: 19,
  },
});
